<template>
  <div class="flex column no-margin settings-css manage" >
    <div
      v-if="!checkPermission('manage_settings') || forbidden" >
      <h1
        class="settings__title back-btn"
        @click="goBack('WAManage')" >
        <SBIcon
          class="mr-2"
          :icon="SBIconList.DOWN_ARROW"
          :color="SBColors.greyPrimary"
          :size="12"
          :rotate="90" />
        <p>Automated responses</p>
      </h1>
      <Forbidden />
    </div>

    <div
      v-else
      class="h-100" >
      <h1
        class="settings__title w-100 flex" >
        <div
          class="back-btn"
          @click="goBack('WAManage')">
          <SBIcon
            class="mr-2"
            :icon="SBIconList.DOWN_ARROW"
            :color="SBColors.greyPrimary"
            :size="12"
            :rotate="90" />
          <p>Automated responses</p>
        </div>
        <div class="greedy" />
        <router-link
          :to="{ name: 'AutomatedResponsesPreferenceSettings' }" >
          <b-button
            class="flex flex-align-center" >
            <SBIcon
              class="mr-1"
              :icon="SBIconList.SETTINGS_LIGHT"
              :color="SBColors.blackPrimary"
              :size="12" />
            <p>Settings</p>
          </b-button>
        </router-link>
      </h1>

      <Spinner
        v-if="loadingInstantReplies"
        class="mt-5"
        :thickness="2" />

      <div
        v-else
        class="automation-wrapper greedy flex">
        <div
          class="response-items dotted-border"
          :class="{dragging: draggingReplyInProgress}" >
          <!-- Non sortable static items -->
          <div
            v-for="(instantReply, index) in instantReplies.filter(reply => reply.static).sort((a, b) => a.keyword === 'default' ? -1 : 1)"
            :key="index"
            class="response-item-wrapper" >
            <div
              :class="{ selected: selectedResponse.id === instantReply.id }"
              class="static response-item flex center-all"
              @click="selectResponse(instantReply, true)" >
              <!-- Static responses - cannot be deleted or have its title edited -->
              <span
                v-if="instantReply.static"
                :ref="`response-title-${instantReply.id}`"
                class="response-keyword greedy ellipsis mr-2 flex flex-align-center" >
                {{ staticReplyData(instantReply).title }}
                <SBIcon
                  v-if="instantReply.static && staticReplyes[instantReply.keyword]"
                  :id="`${instantReply.keyword}-description`"
                  class="ml-1"
                  :icon="SBIconList.INFORMATION"
                  :color="SBColors.greyPrimary"
                  :size="12" />
                <b-popover
                  :target="`${instantReply.keyword}-description`"
                  triggers="hover"
                  placement="bottom"
                  :delay="{ show: 200, hide: 0 } " >
                  {{ staticReplyes[instantReply.keyword].info }}
                </b-popover>
              </span>
              <div :id="`switch-${instantReply.id}`">
                <SwitchCheckbox
                  v-if="!instantReply.new"
                  v-model="instantReply.active"
                  size="micro"
                  class="no-label-margin"
                  :off-outline-color="SBColors.blackUltralight"
                  :value="instantReply.active"
                  @click.native.stop
                  @input="updateReplyStatus(instantReply)" />
              </div>
              <b-popover
                :target="`switch-${instantReply.id}`"
                triggers="hover"
                placement="bottom"
                custom-class="basic-popover"
                :delay="{ show: 200, hide: 0 } " >
                Automated response is turned {{ instantReply.active ? 'ON' : 'OFF' }}
              </b-popover>
            </div>
          </div>

          <!-- Sortable non static items -->
          <draggable
            v-model="sortedInstantReplies"
            handle=".sort-handler-reply"
            draggable=".response-item-wrapper"
            swap-threshold="0.2"
            :animation="150"
            @choose="draggingReplyInProgress = true"
            @unchoose="draggingEndWithDelay"
            @end="handleReplySort" >
            <!-- We can't filter out static replyes directly here like for the previous list of static replies because draggable does not work in that case. -->
            <div
              v-for="(instantReply, index) in sortedInstantReplies"
              :key="index"
              class="response-item-wrapper"
              :class="{ hide: instantReply.hide }" >
              <span
                v-if="!instantReply.new"
                class="sort-handler-reply" >
                <span class="drag-icon" />
              </span>
              <div
                :class="{
                  selected: selectedResponse.id === instantReply.id,
                  static: instantReply.static,
                }"
                class="response-item flex center-all"
                @click="selectResponse(instantReply, true)" >
                <!-- Static responses - cannot be deleted or have its title edited -->
                <span
                  v-if="instantReply.static"
                  :ref="`response-title-${instantReply.id}`"
                  class="response-keyword greedy ellipsis mr-2 flex flex-align-center" >
                  {{ staticReplyData(instantReply).title }}
                  <SBIcon
                    v-if="instantReply.static && staticReplyes[instantReply.keyword]"
                    :id="`${instantReply.keyword}-description`"
                    class="ml-1"
                    :icon="SBIconList.INFORMATION"
                    :color="SBColors.greyPrimary"
                    :size="12" />
                  <b-popover
                    :target="`${instantReply.keyword}-description`"
                    triggers="hover"
                    placement="bottom"
                    :delay="{ show: 200, hide: 0 } " >
                    {{ staticReplyes[instantReply.keyword].info }}
                  </b-popover>
                </span>
                <!-- Non static responses -->
                <!-- @keydown.exact.space.native.prevent="" -->
                <b-input
                  v-else
                  :ref="`response-title-${instantReply.id}`"
                  v-model="instantReply.keyword"
                  :class="{active: selectedResponse.id === instantReply.id}"
                  class="keyword-input ellipsis"
                  placeholder="Enter keyword"
                  @keydown.exact.esc.native="quitNewResponse(instantReply)"
                  @keydown.exact.enter.native="updateTitle(instantReply)"
                  @focusout.native="updateTitle(instantReply)" />
                <!-- <b-dropdown
                  v-if="!instantReply.static"
                  toggle-class="b-dropdown-transparent"
                  menu-class="response-settings"
                  no-caret
                  right
                  :offset="-8"
                  @show="activateReplyMenu(index)"
                  @hide="deactivateReplyMenu(index)" >
                  <template #button-content>
                    <SBIcon
                      class="mr-2 cog"
                      :class="{ force: replyMenuActive === index }"
                      :icon="SBIconList.COG"
                      :color="SBColors.blackPrimary"
                      :size="14"
                      :hover-size="24"
                      :border-radius="100" />
                  </template>
                  <b-dropdown-header
                    @click="stopPropagation" >
                    <h3>Rule</h3>
                    <p class="ellipsis">
                      <span @click="toggleKeywordOptions">{{ instantReply.in_sentence ? 'Message contains' : 'Message is' }}</span> {{ instantReply.keyword }}
                    </p>
                    <div
                      class="keyword-options-wrapper"
                      :class="{visible: keywordOptionsVisible}">
                      <div class="keyword-options">
                        <div
                          class="option flex flex-align-center"
                          @click="setKeywordScope(index, false)" >
                          <SBIcon
                            class="mr-2"
                            :class="{invisible: instantReply.in_sentence}"
                            :icon="SBIconList.DONE_TICK"
                            :color="SBColors.bluePrimary"
                            :size="9"/>
                          <span>Message is</span>
                        </div>
                        <div
                          class="option flex flex-align-center"
                          @click="setKeywordScope(index, true)" >
                          <SBIcon
                            class="mr-2"
                            :class="{invisible: !instantReply.in_sentence}"
                            :icon="SBIconList.DONE_TICK"
                            :color="SBColors.bluePrimary"
                            :size="9"/>
                          <span>Message contains</span>
                        </div>
                      </div>
                    </div>
                  </b-dropdown-header>
                  <b-dropdown-divider />
                  <b-dropdown-item
                    link-class="flex flex-align-center option"
                    @click="stopPropagation" >
                    <SBIcon
                      class="mr-2"
                      :icon="SBIconList.TRASH"
                      :color="SBColors.blackPrimary"
                      :size="14" />
                    Delete
                  </b-dropdown-item>
                </b-dropdown> -->
                <!-- <SBIcon
                  v-if="!instantReply.static && !instantReply.new"
                  class="mr-2 on-parent-hover trash"
                  :icon="SBIconList.TRASH"
                  :color="SBColors.blackUltralight"
                  :size="14"
                  @click.native.stop
                  @click="deleteReply(index)" /> -->
                <div :id="`switch-${instantReply.id}`">
                  <SwitchCheckbox
                    v-if="!instantReply.new"
                    v-model="instantReply.active"
                    size="micro"
                    class="no-label-margin"
                    :off-outline-color="SBColors.blackUltralight"
                    :value="instantReply.active"
                    @click.native.stop
                    @input="updateReplyStatus(instantReply)" />
                </div>
                <b-popover
                  :target="`switch-${instantReply.id}`"
                  triggers="hover"
                  placement="bottom"
                  custom-class="basic-popover"
                  :delay="{ show: 200, hide: 0 } " >
                  Automated response is turned {{ instantReply.active ? 'ON' : 'OFF' }}
                </b-popover>
              </div>
              <div
                v-if="instantReply.error"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ instantReply.error }}</span>
              </div>
              <SBIcon
                v-if="!instantReply.static"
                class="on-parent-hover trash"
                :icon="SBIconList.TRASH"
                :color="SBColors.blackUltralight"
                :size="10"
                :hover-size="22"
                :pointer="true"
                :border-radius="30"
                :hover-icon-color="SBColors.redPrimary"
                @click.native.stop
                @click="deleteReply(instantReply)" />
            </div>
          </draggable>
          <div
            class="new-response-btn"
            :class="{disabled: !canAddNewResponse}"
            @click="addNewResponse">
            <span class="flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.PLUS"
                :color="SBColors.bluePrimary"
                :size="8" />
              <span>New automated response</span>
            </span>
          </div>
        </div>

        <!-- Messages container -->
        <div
          class="response-preview dotted-border"
          :class="{'warn-move': warnMove, dragging: draggingInProgress}" >

          <!-- 1st keyword message -->
          <div class="message-wrapper inbound">
            <div class="message">
              <p v-if="selectedResponse.static && !selectedResponse.new">Contact message</p>
              <p v-else-if="!selectedResponse.keyword">
                <SBIcon
                  :icon="SBIconList.THREE_DOTS"
                  :color="SBColors.blackUltralight"
                  :size="18" />
              </p>
              <template v-else>
                <b-dropdown
                  toggle-class="b-dropdown-transparent"
                  toggle-tag="span"
                  menu-class="response-settings"
                  no-caret >
                  <template #button-content>
                    <span
                      class="message-scope"
                      @click="toggleKeywordOptions">
                      {{ messageTriggerType }} </span>
                  </template>
                  <b-dropdown-item
                    link-class="option flex flex-align-center"
                    @click="setKeywordScope(false, false)" >
                    <SBIcon
                      class="mr-2"
                      :class="{invisible: !(!selectedResponse.in_sentence && !selectedResponse.contains_any)}"
                      :icon="SBIconList.DONE_TICK"
                      :color="SBColors.bluePrimary"
                      :size="9"/>
                    <span :class="{ selected: !selectedResponse.in_sentence && !selectedResponse.contains_any }">Message is</span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    link-class="option flex flex-align-center"
                    @click="setKeywordScope(true, true)" >
                    <SBIcon
                      class="mr-2"
                      :class="{invisible: !(selectedResponse.in_sentence && selectedResponse.contains_any)}"
                      :icon="SBIconList.DONE_TICK"
                      :color="SBColors.bluePrimary"
                      :size="9"/>
                    <span :class="{ selected: selectedResponse.in_sentence && selectedResponse.contains_any }">Message contains</span>
                  </b-dropdown-item>
                  <b-dropdown-item
                    link-class="option flex flex-align-center"
                    @click="setKeywordScope(true, false)" >
                    <SBIcon
                      class="mr-2"
                      :class="{invisible: !(selectedResponse.in_sentence && !selectedResponse.contains_any)}"
                      :icon="SBIconList.DONE_TICK"
                      :color="SBColors.bluePrimary"
                      :size="9"/>
                    <span :class="{ selected: selectedResponse.in_sentence && !selectedResponse.contains_any }">Message contains {{ selectedResponse.keyword.indexOf(' ') >= 0 ? 'phrase' : 'word' }}</span>
                  </b-dropdown-item>
                </b-dropdown>
                <span> {{ selectedResponse.keyword }}</span>
              </template>
            </div>
          </div>

          <!-- All other messages -->
          <draggable
            v-model="currentReplyMessages"
            handle=".sort-handler"
            draggable=".response-element"
            :move="canItBeDropped"
            swap-threshold="0.1"
            :animation="150"
            @choose="startDragging"
            @unchoose="endDragging"
            @end="handleMessagesSorting" >

            <div
              v-for="(message, index) in currentReplyMessages"
              :key="message.id"
              class="response-element"
              :class="{ reply: message.type && message.type === 'reply' }" >

              <!-- User response placeholder -->
              <div
                v-if="message.type && message.type === 'reply'"
                class="message-wrapper inbound" >
                <div
                  :id="`reply-${index}`"
                  class="message contact-answer"
                  @mouseover="showReplyErrorPopover"
                  @mouseleave="hideReplyErrorPopover(`reply-${index}`)" >

                  <!-- Drag handler -->
                  <span
                    class="sort-handler"
                    data-no-popover-trigger >
                    <!-- <icon name="sort"/> -->
                    <span class="drag-icon" />
                  </span>

                  <span class="flex">
                    <p>Contact reply</p>
                    <SBIcon
                      class="error"
                      :icon="SBIconList.ERROR"
                      :color="SBColors.redPrimary"
                      :size="12" />
                  </span>

                  <div
                    class="action-buttons"
                    data-no-popover-trigger>
                    <SBIcon
                      :icon="SBIconList.TRASH"
                      :color="SBColors.blackUltralight"
                      :background-color="SBColors.white"
                      :size="10"
                      :hover-icon-color="SBColors.redPrimary"
                      :hover-color="SBColors.white"
                      :hover-size="22"
                      :border-radius="20"
                      @click="deleteMessage(index)" />
                  </div>
                </div>
                <b-popover
                  v-if="(index === 0) || ((index - 1 >= 0) && currentReplyMessages[index - 1].type === 'reply')"
                  :ref="`reply-${index}`"
                  :target="`reply-${index}`"
                  triggers="manual"
                  placement="bottom"
                  :delay="{ show: 200, hide: 0 } " >
                  {{ (index === 0) ? 'You can’t use Contact reply block as the starting block.' : 'You can’t have multiple Contact reply blocks one after another. This block will be removed.' }}
                </b-popover>
              </div>

              <!-- Automated responses -->
              <div
                v-else
                class="message-wrapper outbound" >
                <div class="message-inner-wrapper">
                  <div
                    class="message"
                    :class="[message.url ? getMediaTypeFromExtension(getFileExtension(message.url)) : '', { 'has-buttons': message.message_type }]" >
                    <!-- Drag handler -->
                    <span class="sort-handler">
                      <span class="drag-icon" />
                    </span>

                    <!-- Text Message -->
                    <template v-if="message.type === 'text'">
                      <p>
                        <MessageContent
                          :content="message.text"
                          :detect-urls="true"
                          :convert-tag-to-button="true"/>
                      </p>
                    </template>
                    <!-- Link Message -->
                    <template v-if="message.type === 'link'">
                      <p>{{ message.text ? `${message.text} ` : '' }}<a
                        :href="message.url"
                        target="_blank">{{ message.url }}</a></p>
                    </template>
                    <!-- File Message -->
                    <template v-if="message.type === 'file'">
                      <MediaMessage
                        :message="message"
                        force-black-loader />
                    </template>

                    <!-- Message Action Buttons -->
                    <div class="action-buttons">
                      <SBIcon
                        :icon="SBIconList.PENCIL"
                        :color="SBColors.blackUltralight"
                        :background-color="SBColors.white"
                        :size="10"
                        :hover-icon-color="SBColors.blackPrimary"
                        :hover-color="SBColors.white"
                        :hover-size="22"
                        :border-radius="20"
                        @click="editMessage(index)" />
                      <!-- Only show if not the first one i.e index > 0 -->
                      <!-- v-if="!sortedInstantReplies[selectedResponse].static || (sortedInstantReplies[selectedResponse].static && index)" -->
                      <SBIcon
                        :icon="SBIconList.TRASH"
                        :color="SBColors.blackUltralight"
                        :background-color="SBColors.white"
                        :size="10"
                        :hover-icon-color="SBColors.redPrimary"
                        :hover-color="SBColors.white"
                        :hover-size="22"
                        :border-radius="20"
                        @click="deleteMessage(index)" />
                    </div>
                  </div>
                  
                  <template v-if="message.message_type === 'QUR'">
                    <div
                      v-for="(button, index) in message.additional_data.items"
                      :key="index"
                      class="message-button flex center-all" >
                      {{ button.title }}
                    </div>
                  </template>

                  <template v-if="message.message_type === 'LIS'">
                    <div
                      class="message-button flex center-all" >
                      <SBIcon
                        class="mr-1"
                        :icon="SBIconList.LIST"
                        :color="SBColors.bluePrimary"
                        :size="12" />
                      <span>{{ message.additional_data.menu_title }}</span>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </draggable>

          <!-- Add block btn -->
          <div
            v-if="canAddNewResponse"
            class="add-block-btn"
            @click="addNewBlock" >
            <SBIcon
              :icon="SBIconList.PLUS"
              :color="SBColors.greyPrimary"
              :size="8" />
            <span class="btn-text">Add block</span>
          </div>
        </div>
      </div>
    </div>

    <AddNewBlockModal
      ref="addNewBlockModal"
      :hide-blocks="hideAddBlocksList"
      :business-id="business.id"
      @new-message="addNewMessage"
      @edit-message="handleMessagesSorting" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import draggable from 'vuedraggable';

import api from '@/helpers/api';
import ToasterMixin from '@/mixins/Toaster';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SwitchCheckbox from '@/components/common/SwitchCheckbox';
import Spinner from '@/components/common/Spinner';
import randomString from '@/helpers/randomString';
import MessageContent from '@/components/common/MessageContent';
import MediaMessage from '@/components/business/chat/partials/MediaMessage';
import { getFileExtension, getMediaTypeFromExtension } from '@/helpers/mediaHelpers';
import AddNewBlockModal from '@/components/business/settings/modals/AddNewBlock.modal';
import Forbidden from '@/components/errors/Forbidden';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';
import CommonActions from '@/components/manage/CommonActions.mixin';

export default {
  name: 'AutomatedResponsesSettings',
  components: {
    draggable,
    SwitchCheckbox,
    SBIcon,
    Spinner,
    MessageContent,
    MediaMessage,
    AddNewBlockModal,
    Forbidden,
  },
  mixins: [
    ToasterMixin,
    UserPermissionsMixin,
    CommonActions,
  ],
  data() {
    return {
      selectedResponse: {},
      keywordOptionsVisible: false,
      sortedInstantReplies: [],
      deletingResponseIndex: null,
      // list of the temp-ids that user canceled creation for but there was not a real id on the backend yet. These will be deleted once the real id is provided
      toBeDeleted: [],
      currentReplyMessages: [],
      warnMove: false,
      staticReplyes: {
        away: {
          info: 'Away message is automatically sent when contacts initiate a chat in WhatsApp and your availability is set on away.',
        },
        default: {
          info: 'Welcome message is automatically sent when contacts initiate a chat in WhatsApp.',
        },
      },
      draggingReplyInProgress: false,
      draggingInProgress: false,
      hideAddBlocksList: [],
      updateInProgress: false, // both focusout and enter key trigger the same method so we need to block the second one if it is already triggered.
      preventRouteChangeWatcher: false,
      forbidden: false,
      SBColors,
      SBIconList,
    };
  },
  computed: {
    ...mapGetters({
      loadingInstantReplies: 'automation/loadingInstantReplies',
      instantReplies: 'automation/instantReplyList',
      instantReplyDeleted: 'automation/instantReplyDeleted',
      instantReplySaved: 'automation/instantReplySaved',
      currentOrganisation: 'organisation/currentOrganisation',
      userPermissions: 'user/userPermissions',
      customFieldChange: 'contact/customFieldChange',
    }),
    business() {
      return this.currentOrganisation.businesses.find(b => b.id === this.$route.params.businessId);
    },
    canAddNewResponse() {
      return !(this.sortedInstantReplies.findIndex(reply => reply.new === true) >= 0);
    },
    countHiddenReplyes() {
      return this.sortedInstantReplies.filter(instantReply => instantReply.hide).length;
    },
    messageTriggerType() {
      let messageTrigger = 'Message is';
      if (this.selectedResponse.in_sentence && this.selectedResponse.contains_any) {
        messageTrigger = 'Message contains';
      } else if (this.selectedResponse.in_sentence && !this.selectedResponse.contains_any) {
        messageTrigger = `Message contains ${this.selectedResponse.keyword.indexOf(' ') >= 0 ? 'phrase' : 'word'}`;
      }
      return messageTrigger;
    },
  },
  watch: {
    $route() {
      if (!this.preventRouteChangeWatcher) {
        this.selectQueryResponse();
      }
    },
    currentReplyMessages() {},
    customFieldChange() {
      this.sortedInstantReplies.forEach((instantReply) => {
        instantReply.messages.forEach((message) => {
          if (this.customFieldChange.newName) {
            message.text = message.text.replace(`{{${this.customFieldChange.oldName}}}`, `{{${this.customFieldChange.newName}}}`);
          } else {
            message.text = message.text.replace(`{{${this.customFieldChange.oldName}}}`, '{{*Deleted tag*}}');
          }
        });
      });
    },
    userPermissions() {
      if (this.checkPermission('manage_settings') && this.business.id) {
        this.fetchInstantReplyList(this.business.id);
      }
    },
    loadingInstantReplies(newState) {
      if (!newState) {
        const sorted = [...this.instantReplies];
        // If we decide to enable drag and drop of the replies so the user can reorder it then we have to drop this sort. But should Default and away be fixed on top even in that case?
        // It could maybe be left because the ordering later is not disrupted. Only the Static replyes are moved to the top.
        // this.sortedInstantReplies = sorted.sort((a, b) => {
        //   if (a.static && b.static) {
        //     return (a.keyword === 'default') ? -1 : 1;
        //   }
        //   return a.static ? -1 : 1;
        // });

        // If we reenable sorting then comment this out and uncomment the above
        this.sortedInstantReplies = sorted.filter(instantReply => !instantReply.static);
        if (this.$route.query['show-response']) {
          this.selectQueryResponse();
        } else {
          this.selectFirstResponse();
        }
      }
    },
    instantReplyDeleted(state) {
      if (state) {
        // If there is no keyword then it means it was a newly added response and the adding was canceled so there is no need to show the toast notification
        if (!this.sortedInstantReplies[this.deletingResponseIndex].new && !this.sortedInstantReplies[this.deletingResponseIndex].hide) {
          this.showToaster('Automated response has been successfully deleted.', null, null, 'OK');
        }
        this.sortedInstantReplies.splice(this.deletingResponseIndex, 1);
        const selectedReply = this.sortedInstantReplies.find(reply => reply.id === this.selectedResponse.id);
        if (
          !selectedReply &&
          this.deletingResponseIndex === this.sortedInstantReplies.length
        ) {
          this.selectLastResponse();
        } else if (
          !selectedReply &&
          this.deletingResponseIndex < this.sortedInstantReplies.length
        ) {
          const newResponseToSelect = this.sortedInstantReplies[this.deletingResponseIndex];
          this.selectResponse(newResponseToSelect);
        }

        this.deletingResponseIndex = null;
        this.$confirmPluginModal.close();
      }
    },
    instantReplySaved(payload) {
      if (payload) {
        let reply = null;

        if (payload.temp_id) {
          reply = this.sortedInstantReplies.find(instantReply => instantReply.id === payload.temp_id);
          if (reply) {
            reply.id = payload.reply_id;
          }
        } else {
          reply = this.sortedInstantReplies.find(instantReply => instantReply.id === payload.reply_id);
        }

        // check if it is canceled maybe
        if (payload.temp_id && this.toBeDeleted.includes(payload.temp_id)) {
          const targetReply = this.sortedInstantReplies.find(r => r.id === payload.reply_id);
          if (targetReply) {
            this.deleteReply(targetReply, true);
          }
          return;
        }

        if (reply) {
          // updating new created message ids
          if (payload.messages) {
            this.selectedResponse.messages.forEach((message, index) => {
              if (this.isTemp(message.id)) {
                if (
                  (payload.messages[index] && message.text === payload.messages[index].text) ||
                  (payload.messages[index] && message.url === payload.messages[index].url)
                ) {
                  message.id = payload.messages[index].id;
                }
              }
            });
          }
        }

        this.handleReplySort();
      }
    },
  },
  mounted() {
    // in case user returns to the page. On first mount business is undefined so this will not trigger
    if (this.checkPermission('manage_settings') && this.business.id) {
      this.fetchInstantReplyList(this.business.id);
    }
    if (this.checkPermission('manage_settings')) {
      this.fetchWhatsAppSettings();
    }
    // TODO - this needs a refactor and fix to be prevented when no reply title is focused so it doesn't trigger if modal is open or something
    document.onkeydown = (e) => {
      const event = e || window.event;
      // switch (event.which || event.keyCode) {
      //   case 38: // up
      //     console.log('up');
      //     if (this.selectedResponse > 0) { // aaa fix this
      //       this.selectedResponse--;
      //     }
      //     this.selectResponse(this.selectedResponse, true);
      //     break;
      //   case 40: // down
      //     if (this.selectedResponse < (this.sortedInstantReplies.length - 1 - this.countHiddenReplyes)) {
      //       this.selectedResponse++;
      //     }
      //     this.selectResponse(this.selectedResponse, true); // aaa fix this
      //     break;
      //   default:
      //     break;
      // }
    };
    window.eventBus.$on('403', this.accessDenied);

    this.finalizeSplashView();
  },
  methods: {
    ...mapActions({
      fetchInstantReplyList: 'automation/fetchInstantReplyList',
      updateReplyStatusRequest: 'automation/updateReplyStatus',
      updateInstantReply: 'automation/updateInstantReply',
      deleteInstantReply: 'automation/deleteInstantReply',
      createInstantReply: 'automation/createInstantReply',
      deleteMessageVuex: 'automation/deleteInstantReplyMessage',
      setInstantReplyOrder: 'automation/setInstantReplyOrder',
      finalizeSplashView: 'organisation/finalizeSplashView',
      fetchWhatsAppSettings: 'system/fetchWhatsAppSettings',
    }),
    getMediaTypeFromExtension,
    getFileExtension,
    addNewBlock() {
      this.$refs.addNewBlockModal.show();
    },
    addNewMessage(message) {
      this.currentReplyMessages.push(message);
      this.handleMessagesSorting();
    },
    addNewResponse() {
      const tempId = `temp-${randomString()}`;
      this.sortedInstantReplies.push({
        new: true,
        id: tempId,
        keyword: '',
        active: false,
        static: false,
        in_sentence: false,
        messages: [],
        oldKeyword: null,
      });

      this.$nextTick(() => {
        this.selectLastResponse();
        this.$refs[`response-title-${tempId}`][0].focus();
      });
    },

    /**
     * When dragging the replys we will check dragging only for "Contact reply" elements because dragging any other element should be allowed since duplicate "Contact reply" element will be styled via css.
     *
     * When dragging an element up then check the target index and index before it.
     * When dragging an element down then check the target element and the one after it.
     */
    canItBeDropped(event) {
      let dragIsOk = true;
      if (
        event.singleTest &&
        (
          (
            event.index === 0 &&
            this.currentReplyMessages[event.index] &&
            this.currentReplyMessages[event.index].type &&
            this.currentReplyMessages[event.index].type === 'reply'
          ) ||
          (
            this.currentReplyMessages[event.index - 1] &&
            this.currentReplyMessages[event.index - 1].type &&
            this.currentReplyMessages[event.index - 1].type === 'reply'
          )
        )
      ) {
        dragIsOk = false;
      }

      // only calculate on dragging contact reply
      if (dragIsOk && event.draggedContext && event.draggedContext.element.type && event.draggedContext.element.type === 'reply') {
        if ( // Do not drop reply to first place
          event.draggedContext.futureIndex === 0
        ) {
          dragIsOk = false;
        } else if ( // dragging DOWN
          dragIsOk &&
          event.draggedContext.futureIndex > event.draggedContext.index &&
          this.currentReplyMessages[event.draggedContext.futureIndex] &&
          this.currentReplyMessages[event.draggedContext.futureIndex].type &&
          this.currentReplyMessages[event.draggedContext.futureIndex].type === 'reply'
        ) {
          dragIsOk = false;
        } else if ( // dragging UP
          dragIsOk &&
          event.draggedContext.futureIndex <= event.draggedContext.index &&
          this.currentReplyMessages[event.draggedContext.futureIndex - 1] &&
          this.currentReplyMessages[event.draggedContext.futureIndex - 1].type &&
          this.currentReplyMessages[event.draggedContext.futureIndex - 1].type === 'reply'
        ) {
          dragIsOk = false;
        }
      }

      if (dragIsOk) {
        this.warnMove = false;
      } else {
        this.warnMove = true;
      }
    },

    collectFormData() {
      const messageOrder = this.selectedResponse.messages.map(message => message.id);

      return {
        messages: this.selectedResponse.messages,
        message_order: messageOrder,
      };
    },
    deleteMessage(index) {
      this.$confirmPluginModal.show({
        title: 'Delete automated response block',
        message: 'Are you sure you want to delete the selected block?',
        confirmButtonText: 'Delete',
        buttonVariant: 'RED',
        confirmCallback: () => {
          if (this.currentReplyMessages[index].id) {
            this.deleteMessageVuex({
              merchant_id: this.business.id,
              reply_id: this.selectedResponse.id,
              message_id: this.currentReplyMessages[index].id,
            });
          }

          this.currentReplyMessages.splice(index, 1);
          this.handleMessagesSorting();
        },
      });
    },
    draggingEndWithDelay() {
      setTimeout(() => {
        this.draggingReplyInProgress = false;
      }, 0);
    },
    editMessage(index) {
      this.$refs.addNewBlockModal.show(this.currentReplyMessages[index]);
    },

    // Old focusout method - TODO obsolete?
    // updateResponse(checkKeywordUpdate = false) {
    //   console.log('update', this.sortedInstantReplies[this.selectedResponse].keyword);
    //   if (
    //     this.selectedResponse !== null &&
    //     this.sortedInstantReplies[this.selectedResponse].keyword &&
    //     this.sortedInstantReplies[this.selectedResponse].messages.length
    //   ) {
    //     if (this.isTemp(this.sortedInstantReplies[this.selectedResponse].id)) {
    //       this.createReply();
    //     } else if (!checkKeywordUpdate || (checkKeywordUpdate && (this.oldKeyword !== this.sortedInstantReplies[this.selectedResponse].keyword))) {
    //       this.updateReply();
    //       // this.oldKeyword = this.sortedInstantReplies[this.selectedResponse].keyword;
    //     }
    //   }
    // },
    updateTitle(automatedResponse) {
      if (this.updateInProgress) {
        // return;
      }

      this.updateInProgress = true;
      this.$set(automatedResponse, 'error', '');
      if ( // case for renaming where user deletes the keyword completely and tries to save it
        !automatedResponse.keyword &&
        automatedResponse.oldKeyword
      ) {
        // Show error and do not save anything - old keyword must remain in case someone makes focus out and selects different reply
        this.$set(automatedResponse, 'error', 'Please enter keyword');
      } else if (!automatedResponse.keyword) { // case when new reply it trying to be added with empty keyword
        this.quitNewResponse(automatedResponse);
      } else if (automatedResponse.new) { // case when creating new automated reply
        // Check if it is a duplicate
        if (this.isDuplicate(automatedResponse)) {
          this.$set(automatedResponse, 'error', 'This keyword is already taken');
          return;
        }
        const payload = {
          temp_id: automatedResponse.id,
          merchant_id: this.business.id,
          data: {
            keyword: automatedResponse.keyword.trim(),
            active: false,
          },
        };
        this.createInstantReply(payload);
        automatedResponse.new = false;
      } else if ( // case when title is being updated from one value to another
        automatedResponse.keyword &&
        automatedResponse.keyword !== automatedResponse.oldKeyword
      ) {
        // Check if it is a duplicate
        if (this.isDuplicate(automatedResponse)) {
          this.$set(automatedResponse, 'error', 'This keyword is already taken');
          return;
        }
        automatedResponse.new = false;
        const payload = {
          merchant_id: this.business.id,
          reply_id: automatedResponse.id,
          data: {
            keyword: automatedResponse.keyword.trim(),
          },
        };

        this.updateInstantReply(payload);
        const targetReply = this.sortedInstantReplies.find(ir => ir.id === automatedResponse.id);
        if (targetReply) {
          targetReply.keyword = automatedResponse.keyword.trim();
        }
        this.$refs[`response-title-${this.selectedResponse.id}`][0].blur();
      }
      this.updateInProgress = false;
    },
    createReply() {

    },
    deleteReply(automatedReply, force = false) {
      this.deletingResponseIndex = this.sortedInstantReplies.findIndex(instantReply => instantReply.id === automatedReply.id);
      if (force) {
        if (this.isTemp(automatedReply.id)) {
          // Just delete local temp reply
          this.sortedInstantReplies.splice(this.deletingResponseIndex, 1);
          return;
        }
        this.deleteInstantReply({
          merchant_id: this.business.id,
          reply_id: this.sortedInstantReplies[this.deletingResponseIndex].id,
        });
      } else {
        this.$confirmPluginModal.show({
          title: 'Delete automated response flow',
          message: `Are you sure you want to delete <b>${this.sortedInstantReplies[this.deletingResponseIndex].keyword}</b>?`,
          confirmButtonText: 'Delete',
          buttonVariant: 'RED',
          confirmCallback: () => {
            this.deleteInstantReply({
              merchant_id: this.business.id,
              reply_id: this.sortedInstantReplies[this.deletingResponseIndex].id,
            });
          },
        });
      }
    },
    /** Selects first automated response which should be a welcome message. If not available use away message or first non static message. */
    selectFirstResponse() {
      const welcomeMessage = this.instantReplies.find(instantReply => instantReply.keyword === 'default');
      if (welcomeMessage) {
        this.selectResponse(welcomeMessage);
        return;
      }

      const awayMessage = this.instantReplies.find(instantReply => instantReply.keyword === 'away');
      if (awayMessage) {
        this.selectResponse(awayMessage);
        return;
      }

      // If none of the above messages were found select the first from sorted instant replies
      if (this.sortedInstantReplies.length) {
        this.selectResponse(this.sortedInstantReplies[0]);
      }
    },
    selectQueryResponse() {
      // try to find the query
      let targetReply;
      if (['default', 'away'].includes(this.$route.query['show-response'])) {
        targetReply = this.instantReplies.find(reply => reply.keyword === this.$route.query['show-response']);
      } else {
        targetReply = this.instantReplies.find(reply => reply.id === this.$route.query['show-response']);
      }
      // if query is available then select it otherwise select first
      if (targetReply) {
        this.selectResponse(targetReply);
      } else {
        this.selectFirstResponse();
      }
    },
    startDragging(e) {
      this.draggingInProgress = true;
      if (e.item.classList.contains('reply')) {
        this.canItBeDropped({
          index: e.oldIndex,
          singleTest: true,
        });
      }
    },
    endDragging() {
      this.draggingInProgress = false;
      this.warnMove = false;
    },
    handleMessagesSorting(e) {
      const newMessageList = [];

      // Rearange messages that have a reply
      this.currentReplyMessages.forEach((message, index) => {
        // Ignore reply messages
        if (message.type && message.type !== 'reply') {
          if (
            this.currentReplyMessages[index + 1] &&
            this.currentReplyMessages[index + 1].type &&
            this.currentReplyMessages[index + 1].type === 'reply'
          ) {
            message.requires_reply = true;
          } else {
            message.requires_reply = false;
          }

          newMessageList.push(message);
        }
      });

      this.selectedResponse.messages = [...newMessageList];

      const payload = {
        merchant_id: this.business.id,
        reply_id: this.selectedResponse.id,
        data: this.collectFormData(),
      };

      this.updateInstantReply(payload);

      this.setAvailableNewBlocks();
    },
    hasTempIDs(list) {
      return !!list.find(li => this.isTemp(li[1]));
    },
    handleReplySort() {
      const order = this.sortedInstantReplies.filter(reply => !reply.new).map((reply, index) => [index, reply.id]);

      const payload = {
        merchant_id: this.business.id,
        data: {
          orderings: order,
        },
      };

      if (!this.hasTempIDs(order)) {
        this.setInstantReplyOrder(payload);
      } // No need for else because handleReplySort will trigger by default as part of instantReplySaved and all the IDs will be real
    },
    setAvailableNewBlocks() {
      this.hideAddBlocksList = [];

      // We decided to allow visibility of Contact reply block btn even if that is the last added block. In case we decide to hide it just uncomment code below.
      // Set contact reply block availability
      // if (this.isContactReplyLast()) {
      //   this.addToHideAddBlocksList('Contact reply');
      // } else {
      //   this.removeFromHideAddBlocksList('Contact reply');
      // }
    },
    isContactReplyLast() {
      if (this.currentReplyMessages.length && this.currentReplyMessages[this.currentReplyMessages.length - 1].type === 'reply') {
        return true;
      }
      return false;
    },
    addToHideAddBlocksList(blockName) {
      if (!this.hideAddBlocksList.includes(blockName)) {
        this.hideAddBlocksList.push(blockName);
      }
    },
    removeFromHideAddBlocksList(blockName) {
      const index = this.hideAddBlocksList.findIndex(item => item === blockName);
      this.hideAddBlocksList.splice(index, 1);
    },
    isTemp(replyId) {
      return replyId.lastIndexOf('temp-', 0) === 0;
    },
    isDuplicate(automatedReply) {
      return this.sortedInstantReplies.findIndex(instantReply => (instantReply.id !== automatedReply.id) && (instantReply.keyword === automatedReply.keyword)) >= 0;
    },
    selectResponse(response, focus) {
      this.preventRouteChangeWatcher = true;
      this.selectedResponse = response;

      if (this.selectedResponse.keyword && !this.selectedResponse.error) {
        this.selectedResponse.oldKeyword = this.selectedResponse.keyword;
      }

      this.currentReplyMessages = [];
      if (this.selectedResponse.messages) {
        this.selectedResponse.messages.forEach((message) => {
          this.currentReplyMessages.push(message);
          if (message.requires_reply) {
            this.currentReplyMessages.push({ type: 'reply' });
          }
        });
      }

      this.setAvailableNewBlocks();

      if (focus) {
        // const { value } = this.$refs[`response-title-${this.selectedResponse}`][0];
        this.$refs[`response-title-${this.selectedResponse.id}`][0].focus();
        // console.log('test:', value);
        // this.$refs[`response-title-${this.selectedResponse}`][0].setSelectionRange(value.length, value.length);

        const query = { ...this.$route.query, 'show-response': response.id };
        this.$router.replace({ query })
          .then(() => {
            this.$nextTick(() => { this.preventRouteChangeWatcher = false; });
          })
          .catch(() => {});
      }
    },
    selectLastResponse() {
      // TODO - fix this on rewrite because we are not using toBeDeleted anymore but it is left here so something else doesnt break at this point.
      const selectResponseIndex = this.sortedInstantReplies.length - 1 - this.toBeDeleted.length; // -2 because the last one is not deleted yet
      if (selectResponseIndex >= 0) {
        this.selectResponse(this.sortedInstantReplies[selectResponseIndex]);
      } else {
        const awayMessage = this.instantReplies.find(instantReply => instantReply.keyword === 'away');
        if (awayMessage) {
          this.selectResponse(awayMessage);
        }
      }
    },
    showReplyErrorPopover(e) {
      // iterate over path and search for id "reoly-{n}"
      // also check the dataset and if you find noPopoverTrigger then abort showing a popover
      e.path.some((element) => {
        const noPopoverFlagFound = Object.keys(element.dataset).includes('noPopoverTrigger');
        if (noPopoverFlagFound) {
          return true;
        }

        // If there is no popover block flag try to find reply id and trigger tooltip show
        const id = element.id;
        const replyIdFound = RegExp('^reply-').test(id);
        if (replyIdFound && this.$refs[id] && this.$refs[id][0]) {
          this.$refs[id][0].$emit('open');
        }
        return replyIdFound;
      });
    },
    hideReplyErrorPopover(id) {
      if (this.$refs[id] && this.$refs[id][0]) {
        this.$refs[id][0].$emit('close');
      }
    },
    // TODO obsolete
    toggleKeywordOptions() {
      this.keywordOptionsVisible = !this.keywordOptionsVisible;
    },
    // TODO obsolete
    closeKeywordOptions() {
      this.keywordOptionsVisible = false;
    },
    quitNewResponse(automatedResponse) {
      if (automatedResponse.new) {
        if (this.isTemp(automatedResponse.id)) {
          // this.toBeDeleted.push(automatedResponse.id);
          this.deleteReply(automatedResponse, true);
        } else {
          this.deleteReply(automatedResponse, true);
        }

        automatedResponse.hide = true;
        automatedResponse.new = false;
        this.selectLastResponse();
      } else { // just reset the keyword
        automatedResponse.keyword = automatedResponse.oldKeyword;
      }
    },
    setKeywordScope(inSentence, containsAny) {
      if (this.selectedResponse.in_sentence === inSentence && this.selectedResponse.contains_any === containsAny) {
        // Don't do anything if there is no change.
        return;
      }
      this.selectedResponse.in_sentence = inSentence;
      this.selectedResponse.contains_any = containsAny;
      // this.closeKeywordOptions();

      const payload = {
        in_sentence: inSentence,
        contains_any: containsAny,
      };

      api('PUT', `organisation/${this.currentOrganisation.id}/business/${this.business.id}/instant-replies/${this.selectedResponse.id}`, payload, null, 'w')
        .catch((error) => {
          console.dir(error);
          this.selectedResponse.in_sentence = !inSentence;
          this.selectedResponse.contains_any = !containsAny;
          this.showToaster('Something went wrong. Please try again.', null, null, 'error');
        });
    },
    staticReplyData(reply) {
      switch (reply.keyword) {
        case 'default': {
          return {
            title: 'Welcome message',
          };
        }
        case 'away': {
          return {
            title: 'Away message',
          };
        }
        default: {
          return {
            title: reply.keyword,
          };
        }
      }
    },
    // TODO - this could be added to root - OBSOLETE?
    stopPropagation(event) {
      event.stopPropagation();
    },
    // TODO obsolete?
    updateReply() {
      const payload = {
        merchant_id: this.business.id,
        reply_id: this.selectedResponse.id,
        data: this.collectFormData(),
      };

      this.updateInstantReply(payload);
    },
    updateReplyStatus(automatedReply) {
      const payload = {
        businessId: this.business.id,
        replyId: automatedReply.id,
        data: {
          active: automatedReply.active,
        },
      };

      this.updateReplyStatusRequest(payload);
    },
    accessDenied() {
      this.forbidden = true;
    },
  },
};
</script>

<style lang="scss">
.automation-wrapper {
  margin-top: 35px;
  overflow: hidden;
  margin-left: -40px;
  height: calc(100% - 101px); // minus the height of header

  .new-response-btn {
    display: inline-block;
    color: $bluePrimary;
    padding: 8px 13px 9px;
    margin-left: 27px;

    span {
      font-weight: 500;
      font-size: 14px;
    }

    &:hover {
      // border-radius: 5px;
      // background-color: $mainBg;
      cursor: pointer;
      color: $blackPrimary;
      .sb-icon .mask {
        background-color: $blackPrimary !important;
      }
    }

    &.disabled {
      color: $blackUltralight;
      pointer-events: none;

      &:hover {
        background-color: unset;
        cursor: default;
      }

      .sb-icon .mask {
        background-color: $blackUltralight !important;
      }
    }
  }

  .response-items {
    padding: 1px 0 13px 0;
    max-width: 600px;
    width: 100%;
    overflow: auto;

    .response-item-wrapper {
      margin: 12px 0 8px;
      padding-right: 40px;
      padding-left: 40px;
      position: relative;

      .sort-handler-reply {
        display: none;
        position: absolute;
        top: calc(50% - 12px);
        right: 12px;
        cursor: pointer;
        width: 18px;
        height: 24px;
        border-radius: 5px;
        box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
        border: solid 1px $greyBorder;
        overflow: hidden;

        .drag-icon {
          display: block;
          height: 100%;
          width: 100%;
          mask: url('~@/assets/img/SB-Icons/drag.svg') no-repeat center !important;
          background-color: $blackUltralight;
        }

        &:hover {
          box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3);

          .drag-icon {
            background-color: $blackPrimary;
          }
        }
      }

      &:hover .sort-handler-reply {
        display: block;
      }

      .sb-icon.on-parent-hover {
        display: none;
        position: absolute;
        top: 6px;
        left: 8px;
        box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
        border: solid 1px $greyBorder;
      }

      &:hover .sb-icon.on-parent-hover {
        display: flex;

        &:hover {
          box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3);
        }
      }

      &.sortable-ghost {
        &::before {
          content: '';
          position: absolute;
          display: block;
          top: 0;
          bottom: 0;
          left: 40px;
          right: 40px;
          background: #F8FAFB;
          opacity: 0.7;
          border-radius: 5px;
        }
      }
    }

    &.dragging {
      .sb-icon.on-parent-hover {
        display: none !important;
      }

      .response-item-wrapper:not(.sortable-chosen) {
        .sort-handler-reply {
          display: none !important;
        }
      }
    }

    .response-item {
      padding: 0 10px;
      border-radius: 5px;
      box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
      border: solid 1px $greyBorder;
      height: 36px;
      cursor: pointer;
      background: $white;

      &:hover {
        box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3);
      }

      &.selected {
        border: solid 1px $blackPrimary;
      }

      &:first-child {
        margin-top: 0;
      }

      .response-keyword {
        font-size: 14px;
        font-weight: 400;
        color: $blackPrimary;
        min-height: 20px;
      }

      .keyword-input {
        cursor: pointer;
        padding: 0;
        border: none;
        box-shadow: none;
        margin-right: 10px;

        &:focus {
          border: none !important;
          box-shadow: none !important;
        }
      }

      // .sb-icon.on-parent-hover {
      //   display: none;
      // }

      // &:hover .sb-icon.on-parent-hover {
      //   display: flex;

      //   &.trash:hover .mask {
      //     background-color: $blackPrimary !important;
      //   }
      // }

      &.static .response-keyword {
        font-weight: 500;
      }

      &.hide {
        display: none !important;
      }
    }
  }

  .response-preview {
    padding: 13px 0;
    max-width: 700px;
    width: 90%;
    overflow: auto;

    .response-element {
      display: block !important;
    }

    .message-wrapper {
      display: flex;
      flex-direction: row-reverse;
      margin-bottom: 12px;
      padding: 0 40px;
      cursor: default;

      .message-inner-wrapper {
        max-width: 75%;
      }

      .message {
        &.image, &.video {
          max-width: 150px;
        }

        * {
          line-height: 1.45;
        }

        p {
          white-space: pre-wrap;
          word-wrap: break-word;
        }

        .media-message .caption {
          white-space: pre-wrap;
          word-wrap: break-word;
        }
      }

      // width: fit-content;
      // max-width: 70%;
      // word-break: break-word;

      .message {
        position: relative;
        padding: 9px 12px 9px 12px;
        border-radius: 18px;
        font-size: 14px;
        font-weight: 400;
        color: $blackPrimary;

        &.has-buttons {
          border-radius: 18px 18px 3px 3px;
        }

        .editor-tag {
          padding: 0px 6px;
          border-radius: 9px;
          border: solid 1px $blackPrimary;
          background-color: #e9f4ff;
          font-size: 12px;
          font-weight: 500;
          color: $blackPrimary;
          white-space: nowrap;

          &.deleted {
            font-weight: 400 !important;
            color: $redPrimary !important;
            border: solid 1px $redPrimary !important;
          }
        }

        a:not(.dropdown-item) {
          color: $blackPrimary !important;
          text-decoration: underline;
        }

        &.image,
        &.video {
          padding: 0 !important;

          .media-message {
            border: 1px solid #dfeeff;
            border-radius: 18px;
          }
        }

        &.audio {
          .media-message {
            overflow: unset !important;

            .audio-player {
              padding: 0 !important;
            }
          }
        }

        &.document .media-file {
          .sb-icon .mask {
            background-color: $blackPrimary !important;
          }
        }

        span {
          word-break: break-all;

          img {
            height: 14px;
            position: relative;
            top: 2px;
          }
        }

        .media-message {
          .media-image {
            width: 150px !important;
            height: 150px !important;

             &.loading {
              position: absolute;
              top: 0;
              background: #dfeeff;
            }
          }

          .media-file a {
            padding: 0 !important;
          }

          .audio-player {
            background-color: #dfeeff;
          }
        }

        .sort-handler {
          display: none;
          position: absolute;
          top: calc(50% - 12px);
          right: -28px;
          cursor: pointer;
          width: 18px;
          height: 24px;
          border-radius: 5px;
          box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
          border: solid 1px $greyBorder;
          overflow: hidden;

          .drag-icon {
            display: block;
            height: 100%;
            width: 100%;
            mask: url('~@/assets/img/SB-Icons/drag.svg') no-repeat center !important;
            background-color: $blackUltralight;
          }

          &:hover {
            box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3);

            .drag-icon {
              background-color: $blackPrimary;
            }
          }
        }

        .action-buttons {
          display: none;
          position: absolute;
          top: calc(50% - 11px);
          left: -67px;

          .sb-icon {
            margin-left: 6px;
            cursor: pointer;
            box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
            border: solid 1px $greyBorder;

            &:hover {
              box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.3);
            }
          }
        }
      }

      &.inbound {
        flex-direction: row;

        .sort-handler {
          right: unset;
          left: -28px;
        }

        .action-buttons {
          right: -37px;
          left: unset;

          .sb-icon {
            margin-left: 0;
            margin-right: 6px;
          }
        }

        .message {
          background-color: #edf0f2;

          .dropdown.b-dropdown {
            display: inline-block;
            vertical-align: baseline;

            span {
              vertical-align: baseline;
            }
          }

          .message-scope {
            font-size: 14px;
            font-weight: 400;
            color: $blackUltralight;
            text-decoration: underline !important;

            &:hover {
              cursor: pointer !important;
              color: $blackPrimary;
            }
          }
        }
      }

      &.outbound {
        .message {
          background-color: #dfeeff;
        }
      }

      &:hover {
        .action-buttons {
          display: flex;
        }

        .sort-handler {
          display: block;
        }
      }

      .message-button {
        padding: 6px 18px;
        border-radius: 3px;
        border: 1px solid #b1cff2;
        background-color: $white;
        color: $bluePrimary;
        margin-top: 3px;
        font-size: 14px;
        font-weight: 400;

        &:last-of-type {
          border-radius: 3px 3px 18px 18px;
        }
      }
    }

    &.dragging {
      .message-wrapper:hover {
        .action-buttons {
          display: none;
        }
      }

      .response-element:not(.sortable-chosen) {
        .sort-handler {
          display: none !important;
        }
      }
    }

    .add-block-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 10px;
      margin: 25px 40px;;
      cursor: pointer;
      border-radius: 6px;
      border: solid 1px #dde6e9;
      background-color: $white;

      span.btn-text {
        font-size: 14px;
        font-weight: 500;
        color: $blackPrimary;
        margin-left: 7px;
      }

      &:hover {
        background-color: $greyUltralight;

        .sb-icon .mask {
          background-color: $blackPrimary !important;
        }
      }
    }
  }

  .dotted-border {
    background-image: linear-gradient($greyBorder 50%, rgba(255,255,255,0) 0%);
    background-position: right;
    background-size: 1px 12px;
    background-repeat: repeat-y;
  }
}

.b-dropdown {
  .btn.b-dropdown-transparent {
    border: none !important;
    box-shadow: none !important;
    background: none !important;
    padding: 0 !important;

    &:hover {
      background: none !important;
    }
  }

  hr {
    margin: 0 !important;
  }

  .response-settings {
    // width: 300px;

    // .dropdown-header {
    //   padding: 20px 13px !important;
    //   cursor: default !important;

    //   h3 {
    //     padding: 0 !important;
    //     color: $blackPrimary;
    //     font-size: 14px;
    //     font-weight: 500;
    //   }

    //   p {
    //     padding: 8px 0 0 !important;
    //     font-size: 14px;
    //     font-weight: 400;
    //     color: $bluePrimary;

    //     span {
    //       font-size: 14px;
    //       font-weight: 400;
    //       color: $blackUltralight;
    //       text-decoration: underline !important;

    //       &:hover {
    //         cursor: pointer !important;
    //         color: $blackPrimary;
    //       }
    //     }
    //   }

    //   .keyword-options-wrapper {
    //     overflow: hidden;
    //     max-height: 0px;
    //     transition: all 0.3s ;

    //     &.visible {
    //       max-height: 85px;
    //     }

    //     .keyword-options {
    //       margin-top: 10px;
    //       padding: 6px;
    //       background: $greyUltralight;
    //       border-radius: 5px;

    //       .option {
    //         padding: 5px 8px;
    //         cursor: pointer;
    //         color: $blackPrimary;
    //         margin-bottom: 10px;

    //         &:last-of-type {
    //           margin-bottom: 0;
    //         }

    //         &:hover {
    //           background: $greyPrimary;
    //           border-radius: 4px;
    //         }
    //       }
    //     }
    //   }
    // }

    li {
      a.dropdown-item.option {
        // margin: 5px !important;
        // width: unset !important;
        // border-radius: 5px !important;
      }
    }
  }
}

.sortable-ghost {
  position: relative;
  opacity: 100%;

  &::before {
    content: '';
    position: absolute;
    display: block;
    top: 0;
    bottom: 0;
    left: 40px;
    right: 40px;
    background: #F8FAFB;
    opacity: 0.7;
    border-radius: 18px;
  }

  .message-wrapper {
    opacity: 0 !important;
  }

  &.response-item-wrapper {
    .sort-handler-reply,
    .response-item {
      opacity: 0 !important;
    }
  }
}

.warn-move .reply.sortable-ghost::before {
  background: #FFF5F6;
}

.reply {
  .message-wrapper {
    .message {
      .sb-icon.error {
        display: none;
      }
    }
  }
}

.reply:first-child,
.reply + .reply {
  .message-wrapper {
    .message {
      background-color: #ffebec !important;
      color: #ff5b61;
      .sb-icon.error {
        margin: 0 0 0 7px;
        display: block;
      }
    }
  }
}

 .error span {
  font-size: 14px;
  font-weight: 400;
  color: #ff5b61;
}

.b-dropdown .dropdown-menu .dropdown-item,
.b-dropdown .dropdown-toggle {
  text-transform: none;
}

.b-dropdown .dropdown-menu .dropdown-item .selected {
  color: $bluePrimary;
}

</style>
